import { Injectable, inject } from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {firstValueFrom, mergeMap, withLatestFrom} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {Store} from "@ngrx/store";
import {Router} from "@angular/router";
import {InvitationCheckDto, RegisterUserDto} from "@server-models";
import {SharedModalControllerService} from "@shared/services/shared-modal-controller.service";
import {TeamRegisterSideEffectsService} from "@team/pages/register/services/team-register-side-effects.service";
import {TeamRegisterActions} from "@team/pages/register/store/team-register.actions-type";
import {TeamRegisterApiService} from "@team/pages/register/services/team-register-api.service";
import {IRegisterGuestAction} from "@team/pages/register/interfaces/register-guest-action.interface";
import {SharedLoginActions} from "@shared/components/login/store/shared-login.actions-type";
import {SharedLoginSelectors} from "@shared/components/login/store/shared-login.selector-type";
import {AlertBaseControllerService} from "@shared/services/shared-alert-controller.service";
import {TranslateService} from "@ngx-translate/core";

@Injectable({
  providedIn: 'root'
})
export class TeamRegisterEffects {
  private _store = inject(Store);
  private _actions$ = inject(Actions);
  private _teamLoginApiService = inject(TeamRegisterApiService);
  private _loginTeamSideEffectsService = inject(TeamRegisterSideEffectsService);
  private _sharedModalControllerService = inject(SharedModalControllerService);
  private _alertControllerService = inject(AlertBaseControllerService);
  private _translateService = inject(TranslateService);
  private _router = inject(Router);


  // Registration Guest - Start -

  initRegistrationGuest$ = createEffect(() => this._actions$.pipe(
      ofType(TeamRegisterActions.initRegistrationGuest),
      mergeMap(action => {
        return [TeamRegisterActions.initRegistrationGuestDialogOpen({ currentEmail: action.currentEmail })]
      }),
      catchError((error) => [TeamRegisterActions.initRegistrationGuestFail({ error })])
    )
  );

  initRegistrationGuestDialogOpen$ = createEffect(() => this._actions$.pipe(
      ofType(TeamRegisterActions.initRegistrationGuestDialogOpen),
      mergeMap((action: { currentEmail: string | undefined }) => {
        const controlEmail = action.currentEmail ? action.currentEmail : "";

        this._loginTeamSideEffectsService.askRegistrationData(controlEmail);
        return [TeamRegisterActions.initRegistrationGuestSuccess()]
      }),
    )
  );

  registerGuestPinRequest$ = createEffect(() => this._actions$.pipe(
      ofType(TeamRegisterActions.registerGuestPinRequest),
      mergeMap((action: { registerGuest: IRegisterGuestAction, isLoadingId?: string | number }) => {
        return this._teamLoginApiService.requestGuestPin(action.registerGuest.email).pipe(
          mergeMap(() =>
            [TeamRegisterActions.registerGuestPinRequestSuccess({
              isRetry: action.registerGuest.isRetry,
              email: action.registerGuest.email,
              passwordBase64: action.registerGuest.passwordBase64
            })]
          ),
          catchError((error) =>
            [TeamRegisterActions.registerGuestPinRequestFail({ error: error })]
          )
        )
      })
    )
  );

  registerGuestPinRequestSuccess$ = createEffect(() => this._actions$.pipe(
      ofType(TeamRegisterActions.registerGuestPinRequestSuccess),
      mergeMap((action: IRegisterGuestAction) => {
        if (!action.isRetry) {
          this._loginTeamSideEffectsService.askRegistrationPinGuest(action.passwordBase64, action.email);
        }
        return [TeamRegisterActions.registerGuestPinDialog()]
      }),
    )
  );

  registerGuestAsUser$ = createEffect(() => this._actions$.pipe(
      ofType(TeamRegisterActions.registerGuestAsUser),
      mergeMap((action: { data: RegisterUserDto, isLoadingId: number | string, isRetry?: boolean }) => {
        return this._teamLoginApiService.requestGuestAsUser(action.data.pinFromEmail!, action.data.email!, action.data.passwordBase64!).pipe(
          map(() => {
              return TeamRegisterActions.registerGuestAsUserSuccess({
                isRetry: action.isRetry!,
                email: action.data.email!,
                passwordBase64: action.data.passwordBase64!
              })
            }
          ),
          catchError((error) =>
            [TeamRegisterActions.registerGuestAsUserFail({ error: error })]
          )
        )
      })
    )
  );

  registerGuestAsUserSuccess$ = createEffect(() => this._actions$.pipe(
      ofType(TeamRegisterActions.registerGuestAsUserSuccess),
      mergeMap((action: {
          isRetry: boolean;
          email: string;
          passwordBase64: string;
        }) => {
          this._router.navigate(['/']).then(() => this._sharedModalControllerService.closeModal());
          return [SharedLoginActions.login({
            email: action.email!,
            passwordBase64: action.passwordBase64
          })]
        }
      )
    )
  );

  // Invitation of users

  invitationCodeGuestCheck$ = createEffect(() => this._actions$.pipe(
      ofType(TeamRegisterActions.checkInvitationCode),
      mergeMap((action: { code: string }) => this._teamLoginApiService.requestInvitationCodeCheck(action.code).pipe(
          mergeMap((response: InvitationCheckDto) => {
              if (response.isRevoked) {
                return this._alertControllerService.observableAlert({
                  header: this._translateService.instant('TEAM.WELCOME.ERRORS.TITLES.REVOKED_CODE'),
                  message: this._translateService.instant('TEAM.WELCOME.ERRORS.DESCRIPTIONS.REVOKED_CODE'),
                  buttons: [{ text: this._translateService.instant("TEAM.WELCOME.BUTTONS.OK") }],
                }).pipe(
                  map(() => TeamRegisterActions.checkInvitationCodeFail({ error: new Error('Code revoked') }))
                );
              } else {
                return [TeamRegisterActions.checkInvitationCodeSuccess()]
              }
            }
          ),
          catchError((error) => {
              this._sharedModalControllerService.closeModal();
              return [TeamRegisterActions.checkInvitationCodeFail({ error: error })]
            }
          )
        )
      )
    )
  );

  invitationCodeGuestAccept$ = createEffect(() => this._actions$.pipe(
      ofType(TeamRegisterActions.checkInvitationIdentity),
      mergeMap((action: { code: string, lastName: string, pin: string }) => {
        return this._teamLoginApiService.requestInvitationCodeAccept(action.code, action.lastName, action.pin).pipe(
          mergeMap((data) => {
            return [TeamRegisterActions.checkInvitationIdentitySuccess({ tenantId: data.tenantId! })];
          }),
          catchError((error) => {
            return [TeamRegisterActions.checkInvitationIdentityFail({ error: error })];
          })
        )
      })
    )
  );

  invitationCodeGuestAcceptSuccess$ = createEffect(() => this._actions$.pipe(
      ofType(TeamRegisterActions.checkInvitationIdentitySuccess),
      withLatestFrom(this._store.select(SharedLoginSelectors.selectTenant)),
      mergeMap(([{ tenantId }, tenant]) => [SharedLoginActions.refreshToken({ tenantId: tenantId ?? tenant?.tenantId })]),
      tap(async () => {
        await firstValueFrom(this._actions$.pipe(ofType(SharedLoginActions.refreshTokenSuccess)));
        this._router.navigate(['team', 'init'], { replaceUrl: true }).then();
      })
    )
  );
}

